import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { richTextToHtml } from '../functions/rich_text.helper';
import './rich_text_editor.css';

const ToolbarButton = ({ active, disabled, label, onClick, children, }) => (
    <button
    type='button'
    disabled={disabled}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={label}
    className={`rich-text-toolbar-btn${active ? ' active' : ''}`}
    >
        {children}
    </button>
);

// Drop-in replacement for the old `<ReactQuill theme='snow' .../>` editable usage.
// Accepts a stored value in ANY legacy format (plain text / Quill Delta JSON / HTML)
// and always calls onChange with a plain HTML string going forward.
const RichTextEditor = ({ value, onChange = () => {}, placeholder = '', style, className, }) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({ openOnClick: false, autolink: true, }),
            Placeholder.configure({ placeholder, }),
        ],
        content: richTextToHtml(value),
        onUpdate: ({ editor, }) => {
            onChange(editor.isEmpty ? '' : editor.getHTML());
        },
    });

    // Keep the editor in sync when `value` is loaded/replaced asynchronously
    // (e.g. after an edit form's fetch resolves), without fighting the user's typing.
    useEffect(() => {
        if (!editor) return;
        const html = richTextToHtml(value);
        if (html !== editor.getHTML()) {
            editor.commands.setContent(html, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, value]);

    useEffect(() => () => editor?.destroy(), [editor]);

    if (!editor) return null;

    const setLink = () => {
        const previous_url = editor.getAttributes('link').href;
        const url = window.prompt('URL', previous_url ?? 'https://');
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url, }).run();
    }

    return (
        <div className={`rich-text-editor${className ? ` ${className}` : ''}`} style={style}>
            <div className='rich-text-toolbar'>
                <ToolbarButton label='Bold' active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></ToolbarButton>
                <ToolbarButton label='Italic' active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></ToolbarButton>
                <ToolbarButton label='Underline' active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></ToolbarButton>
                <ToolbarButton label='Strikethrough' active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></ToolbarButton>
                <span className='rich-text-toolbar-divider' />
                <ToolbarButton label='Bullet list' active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>&bull; List</ToolbarButton>
                <ToolbarButton label='Numbered list' active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</ToolbarButton>
                <span className='rich-text-toolbar-divider' />
                <ToolbarButton label='Link' active={editor.isActive('link')} onClick={setLink}>Link</ToolbarButton>
                <ToolbarButton label='Clear formatting' onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>Clear</ToolbarButton>
            </div>
            <EditorContent editor={editor} className='rich-text-content' />
        </div>
    );
}

export default RichTextEditor;
